import React from 'react';
import { Switch as SwitchHU } from '@headlessui/react';
import tw from 'twin.macro';
import styled from '@emotion/styled';

type Size = 'lg' | 'sm' | 'md' | 'xs';

interface Props {
  label?: any;
  onChange: (b: boolean) => void;
  checked: boolean | undefined;
  size?: Size;
  positionLabel?: 'left' | 'right';
  fullWidth?: boolean;
  disabled?: boolean;
}

const Wrapper = styled.div`
  ${tw`flex items-center`}
`;

const buttonSizes: Record<Size, any> = {
  xs: tw`h-3.5 w-6`,
  sm: tw`h-4 w-7 `,
  md: tw`h-5 w-9 `,
  lg: tw`h-5 w-9 `,
};

const containerSize: Record<Size, any> = {
  xs: tw`h-2.5 w-2.5`,
  sm: tw`h-3 w-3`,
  md: tw`h-4 w-4`,
  lg: tw`h-4 w-4`,
};

const translateSize: Record<Size, any> = {
  xs: tw`translate-x-2.5`,
  sm: tw`translate-x-3`,
  md: tw`translate-x-4`,
  lg: tw`translate-x-4`,
};

export function SwitchToggle(props: Props) {
  const {
    label,
    checked,
    onChange,
    size = 'md',
    fullWidth = false,
    positionLabel = 'right',
    ...rest
  } = props;
  const labelComponent = (
    <SwitchHU.Label
      as="span"
      css={[
        positionLabel === 'left' && tw`mr-1.5`,
        positionLabel === 'right' && tw`ml-1.5`,
        size === 'xs' && tw`text-xs`,
        fullWidth && tw`flex-grow`,
      ]}
    >
      <span
        role="presentation"
        css={[
          tw`cursor-pointer text-sm text-neutral-intense`,
          props.disabled && tw`cursor-not-allowed text-neutral-supershy`,
        ]}
      >
        {label}
      </span>
    </SwitchHU.Label>
  );
  return (
    <SwitchHU.Group as={Wrapper} {...rest}>
      {positionLabel === 'left' && labelComponent}
      <SwitchHU
        css={[
          tw`relative inline-flex shrink-0 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none`,
          buttonSizes[size],
          checked && tw`bg-brand-intense`,
          !checked && tw`bg-neutral-moderate`,
        ]}
        checked={checked || false}
        disabled={rest.disabled}
        onChange={() => onChange(!checked)}
      >
        <span
          aria-hidden="true"
          css={[
            tw`pointer-events-none inline-block rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`,
            containerSize[size],
            checked && translateSize[size],
            !checked && tw`translate-x-0`,
          ]}
        />
      </SwitchHU>
      {positionLabel === 'right' && labelComponent}
    </SwitchHU.Group>
  );
}
